import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalHeader, ModalContent } from '@creditornot/cbt-modal';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getMerchantPayoutLevelByVenueId } from 'apis/merchant-payout-service/api';
import { MerchantPayoutLevelInfo, PayoutLevel } from 'apis/merchant-payout-service/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import PageHeader from 'common/components/PageHeader';
import { useVenueContext } from 'modules/venue/VenueContext';

import InvoiceList from './InvoiceList';
import PayoutReportList from './PayoutReportList';
import { VenuePayoutReportActions } from './VenuePayoutReportActions';
import { VenuePayoutReportTypeSwitch } from './VenuePayoutReportTypeSwitch';
import { AnnualReportDownloadStatus, VenuePayoutReportType } from './types';

const Root = styled.div`
  /* Add space to the bottom so user can scroll the content past the Intercom bubble. */
  padding-block-end: 80px;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledAlert = styled(Alert)`
  border-radius: 0;
  margin-block-start: 16px;
`;

const StyledWoltModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  padding: 0;
  width: 500px;
`;

const AlertContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const VenuePayoutReportsContainer = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const AnnualReportGenerationsModal = (props: {
  isOpen: boolean;
  status?: AnnualReportDownloadStatus;
  onCloseClick: () => void;
}) => (
  <StyledWoltModal isOpen={props.isOpen} onRequestClose={props.onCloseClick}>
    <ModalHeader onCloseClick={props.onCloseClick}>
      <FormattedMessage id="payout-reports.annual_report_modal_header" />
    </ModalHeader>
    <ModalContent>
      {props.status === AnnualReportDownloadStatus.Downloading && (
        <Alert variant="success">
          <FormattedMessage id="payout-reports.annual_report_modal_content_downloading" />
        </Alert>
      )}
      {props.status === AnnualReportDownloadStatus.Error && (
        <Alert variant="error">
          <FormattedMessage id="payout-reports.annual_report_modal_content_error" />
        </Alert>
      )}
    </ModalContent>
  </StyledWoltModal>
);

export const VenuePayoutReports = ({
  venue,
  venuePayoutReportType,
  annualReportDownloadStatus,
  setAnnualReportDownloadStatus,
}: {
  venue: Venue;
  venuePayoutReportType: VenuePayoutReportType;
  annualReportDownloadStatus: AnnualReportDownloadStatus;
  setAnnualReportDownloadStatus: (status: AnnualReportDownloadStatus) => void;
}) => {
  return (
    <VenuePayoutReportsContainer>
      {venuePayoutReportType === 'reports' ? <PayoutReportList venue={venue} /> : <InvoiceList venueId={venue.id} />}
      <AnnualReportGenerationsModal
        onCloseClick={() => setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial)}
        isOpen={!!annualReportDownloadStatus}
        status={annualReportDownloadStatus}
      />
    </VenuePayoutReportsContainer>
  );
};

const PayoutReportPage = () => {
  const venue = useVenueContext();
  const [mpsMerchant, setMpsMerchant] = useState<MerchantPayoutLevelInfo | null>(null);

  const intl = useIntl();
  const [venuePayoutReportType, setVenuePayoutReportType] = useState<VenuePayoutReportType>('reports');
  const [annualReportDownloadStatus, setAnnualReportDownloadStatus] = useState<AnnualReportDownloadStatus>(
    AnnualReportDownloadStatus.Initial,
  );

  useEffect(() => {
    if (!venue) {
      return;
    }
    getMerchantPayoutLevelByVenueId(venue.id)
      .then(setMpsMerchant)
      .catch(() => toast('Failed to fetch merchant info'));
  }, [venue]);

  return (
    <Root>
      {mpsMerchant && mpsMerchant.payoutLevel === PayoutLevel.MERCHANT && (
        <StyledAlert variant="info" title={intl.formatMessage({ id: 'tax-reporting.title' })}>
          <AlertContent>
            <p>
              This venue has moved to payouts at the merchant level, please{' '}
              <a target="_blank" href={`/merchant/${mpsMerchant.merchantId}/reports`}>
                click
              </a>{' '}
              here to view the documents
            </p>
          </AlertContent>
        </StyledAlert>
      )}
      <PageHeader title={intl.formatMessage({ id: 'navigation.payout-reports' })}>
        <VenuePayoutReportActions venue={venue} setAnnualReportDownloadStatus={setAnnualReportDownloadStatus} />
      </PageHeader>
      <VenuePayoutReportTypeSwitch
        venuePayoutReportType={venuePayoutReportType}
        setVenuePayoutReportType={setVenuePayoutReportType}
      />
      <VenuePayoutReports
        venue={venue}
        venuePayoutReportType={venuePayoutReportType}
        annualReportDownloadStatus={annualReportDownloadStatus}
        setAnnualReportDownloadStatus={setAnnualReportDownloadStatus}
      />
    </Root>
  );
};

export default PayoutReportPage;
